import { ITEM_TYPES } from '../../constants';
import { num } from './dates';
import { debtPaymentDue } from './debt';

/**
 * A stored amount as a monthly figure. Items hold either a monthly or an annual
 * amount, with a `frequency` flag beside it saying which.
 */
export const monthlyAmount = (value, frequency) => {
    const amount = parseFloat(value || 0);
    return frequency === 'annually' ? amount / 12 : amount;
};

/**
 * The same, as an annual figure — for the FIRE page, which reasons in annual
 * money. Deliberately not `monthlyAmount(v, f) * 12`: dividing by twelve and
 * multiplying back does not always return the original float.
 */
export const annualAmount = (value, frequency) => {
    const amount = parseFloat(value) || 0;
    return frequency === 'annually' ? amount : amount * 12;
};

/**
 * One item's contribution to monthly cashflow. The `mode` says where the
 * caller sits, which only changes how debt is read:
 *
 *  'stated'    — the plan as it stands at `asOf` (default now). Used by the
 *                dashboard, FIRE and Sankey, none of which carry simulation
 *                state. A missing payment is derived from the loan's terms.
 *
 *  'simulated' — inside a running month loop, where each item carries a
 *                `simulated*TermRemaining` countdown and its payments have
 *                already been resolved at t0.
 *
 * Either way a debt stops being paid once its term ends — 'simulated' reads its
 * countdown, 'stated' asks debtPaymentDue.
 */
export const itemMonthlyCashflow = (item, { mode = 'stated', asOf = null } = {}) => {
    const simulated = mode === 'simulated';
    let cashflow = 0;

    if (item.type === ITEM_TYPES.REAL_ESTATE) {
        if (item.isRented) {
            cashflow += monthlyAmount(item.monthlyRentalIncome, item.amountFrequency);
        } else {
            cashflow -= monthlyAmount(item.monthlyCosts, item.amountFrequency);
        }
        if (item.hasMortgage) {
            if (simulated) {
                if (item.simulatedMortgageTermRemaining > 0) {
                    cashflow -= num(item.mortgageMonthlyPayment);
                }
            } else {
                cashflow -= debtPaymentDue(item, asOf);
            }
        }
    } else {
        let effectiveAmount = 0;
        if (item.type === ITEM_TYPES.INCOME) effectiveAmount = monthlyAmount(item.monthlyIncome, item.amountFrequency);
        else if (item.type === ITEM_TYPES.EXPENSE) effectiveAmount = monthlyAmount(item.monthlyExpense, item.amountFrequency);
        else if (item.type === ITEM_TYPES.OTHER_INVESTMENT) effectiveAmount = monthlyAmount(item.monthlyContribution, item.contributionFrequency);

        switch (item.type) {
            case ITEM_TYPES.OTHER_INVESTMENT:
                if (item.isIncome) cashflow += (num(item.currentValue) * num(item.annualIncomeYield)) / 12;
                cashflow -= effectiveAmount;
                break;
            case ITEM_TYPES.EXPENSE:
                cashflow -= effectiveAmount;
                break;
            case ITEM_TYPES.LOAN:
                if (simulated) {
                    if (item.simulatedLoanTermRemaining > 0) {
                        cashflow -= num(item.monthlyPayment);
                    }
                } else {
                    cashflow -= debtPaymentDue(item, asOf);
                }
                break;
            case ITEM_TYPES.INCOME:
                cashflow += effectiveAmount;
                break;
            default:
                break;
        }
    }
    return isNaN(cashflow) ? 0 : cashflow;
};

/** Monthly interest thrown off by the cash pile. */
export const liquidMonthlyInterest = (liquidAssets, annualRate) => {
    const amount = num(liquidAssets);
    const rate = num(annualRate);
    return (amount > 0 && rate > 0) ? (amount * rate) / 12 : 0;
};
